import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Email as EmailIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { useClaimEvidenceService } from '../../services/claimEvidenceService';
import type { ClaimEvidenceDto, EvidenceType } from '../../types/claimEvidence';

interface ClaimEvidenceModalProps {
  open: boolean;
  onClose: () => void;
  claimId: number;
  clientLabel?: string;
  serviceLabel?: string;
  assignedSpecialistName?: string;
  /** Si es false, solo consulta (sin agregar/eliminar). Default true. */
  allowMutations?: boolean;
  onEvidenceUpdate?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`evidence-tabpanel-${index}`}
      aria-labelledby={`evidence-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ClaimEvidenceModal: React.FC<ClaimEvidenceModalProps> = ({
  open,
  onClose,
  claimId,
  clientLabel,
  serviceLabel,
  assignedSpecialistName,
  allowMutations = true,
  onEvidenceUpdate
}) => {
  const { 
    getClaimEvidences, 
    uploadAndCreateEvidence, 
    createEmailEvidence, 
    deleteEvidence 
  } = useClaimEvidenceService();
  
  const [tabValue, setTabValue] = useState(0);
  const [evidences, setEvidences] = useState<ClaimEvidenceDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Estados para agregar evidencia FILE
  const [file, setFile] = useState<File | null>(null);
  const [fileDescription, setFileDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Estados para agregar evidencia EMAIL
  const [emailData, setEmailData] = useState({
    subject: '',
    from: '',
    to: '',
    body: '',
    date: new Date()
  });
  const [emailDescription, setEmailDescription] = useState('');
  const [evidenceType, setEvidenceType] = useState<EvidenceType>('FILE');
  const [emailViewModalOpen, setEmailViewModalOpen] = useState(false);
  const [selectedEmailEvidence, setSelectedEmailEvidence] = useState<ClaimEvidenceDto | null>(null);

  // Cargar evidencias al abrir el modal
  useEffect(() => {
    if (open && claimId) {
      loadEvidences();
      if (!allowMutations) setTabValue(0);
    } else {
      // Resetear estados al cerrar
      setEvidences([]);
      setTabValue(0);
      setFile(null);
      setFileDescription('');
      setEmailData({
        subject: '',
        from: '',
        to: '',
        body: '',
        date: new Date()
      });
      setEmailDescription('');
    }
  }, [open, claimId]);

  const loadEvidences = async () => {
    if (!claimId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await getClaimEvidences(claimId);
      setEvidences(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar evidencias');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      // Validar tamaño (50MB)
      if (selectedFile.size > 50 * 1024 * 1024) {
        setError('El archivo excede el tamaño máximo de 50MB');
        return;
      }
      
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleUploadFile = async () => {
    if (!file) {
      setError('Selecciona un archivo');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      await uploadAndCreateEvidence(claimId, file, fileDescription);
      setSuccess('Evidencia de archivo creada exitosamente');
      setFile(null);
      setFileDescription('');
      // Resetear input file
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      // Recargar evidencias
      await loadEvidences();
      if (onEvidenceUpdate) onEvidenceUpdate();
    } catch (err: any) {
      setError(err.message || 'Error al subir evidencia');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateEmail = async () => {
    if (!emailData.from || !emailData.to) {
      setError('Los campos "De" y "Para" son obligatorios');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      await createEmailEvidence(
        claimId,
        {
          subject: emailData.subject,
          from: emailData.from,
          to: emailData.to,
          body: emailData.body,
          date: emailData.date.toISOString()
        },
        emailDescription
      );
      setSuccess('Evidencia de correo creada exitosamente');
      setEmailData({
        subject: '',
        from: '',
        to: '',
        body: '',
        date: new Date()
      });
      setEmailDescription('');
      
      // Recargar evidencias
      await loadEvidences();
      if (onEvidenceUpdate) onEvidenceUpdate();
    } catch (err: any) {
      setError(err.message || 'Error al crear evidencia de correo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteEvidence = async (evidenceId: number) => {
    if (!window.confirm('¿Estás seguro de eliminar esta evidencia?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteEvidence(claimId, evidenceId);
      setSuccess('Evidencia eliminada exitosamente');
      await loadEvidences();
      if (onEvidenceUpdate) onEvidenceUpdate();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar evidencia');
    } finally {
      setLoading(false);
    }
  };


  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Evidencias de la Cita
            </Typography>
            {(clientLabel || serviceLabel || assignedSpecialistName !== undefined) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {[
                  clientLabel?.trim(),
                  serviceLabel?.trim(),
                  assignedSpecialistName?.trim()
                    ? `Canalizado a: ${assignedSpecialistName.trim()}`
                    : clientLabel || serviceLabel
                      ? 'Sin especialista asignado'
                      : null
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {!allowMutations && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Asigna un especialista al servicio antes de agregar o eliminar evidencias. Solo consulta
            disponible.
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label={`Ver Evidencias (${evidences.length})`} />
            {allowMutations && <Tab label="Agregar Evidencia" />}
          </Tabs>
        </Box>

        {/* Tab: Ver Evidencias */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : evidences.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No hay evidencias asociadas a esta cita.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '500px' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Creado por</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {evidences.map((evidence) => (
                    <TableRow key={evidence.id} hover>
                      <TableCell>
                        <Chip
                          label={evidence.evidenceType}
                          size="small"
                          color={evidence.evidenceType === 'FILE' ? 'primary' : 'secondary'}
                          icon={evidence.evidenceType === 'FILE' ? <AttachFileIcon /> : <EmailIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: '200px' }}>
                          {evidence.description || evidence.fileName || evidence.emailSubject || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(evidence.createdAt).toLocaleDateString('es-MX')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {evidence.createdBy?.fullName || evidence.createdBy?.username || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {evidence.evidenceType === 'FILE' && evidence.fileUrl && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              href={evidence.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<AttachFileIcon />}
                            >
                              Abrir
                            </Button>
                          )}
                          {evidence.evidenceType === 'EMAIL' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => {
                                setSelectedEmailEvidence(evidence);
                                setEmailViewModalOpen(true);
                              }}
                              startIcon={<EmailIcon />}
                            >
                              Ver
                            </Button>
                          )}
                          {allowMutations && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteEvidence(evidence.id)}
                              title="Eliminar evidencia"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab: Agregar Evidencia */}
        {allowMutations && (
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Tipo de Evidencia</InputLabel>
              <Select
                value={evidenceType}
                onChange={(e) => setEvidenceType(e.target.value as EvidenceType)}
                label="Tipo de Evidencia"
              >
                <MenuItem value="FILE">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachFileIcon fontSize="small" />
                    Archivo
                  </Box>
                </MenuItem>
                <MenuItem value="EMAIL">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" />
                    Correo Electrónico
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {evidenceType === 'FILE' ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 3, textAlign: 'center' }}>
                    <input
                      id="file-input"
                      type="file"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      accept="image/*,application/pdf,.doc,.docx"
                    />
                    <label htmlFor="file-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        disabled={uploading}
                      >
                        Seleccionar Archivo
                      </Button>
                    </label>
                    {file && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Archivo seleccionado:</strong> {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Tamaño: {formatFileSize(file.size)} | Tipo: {file.type || 'Desconocido'}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Tamaño máximo: 50MB
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción (opcional)"
                    multiline
                    rows={3}
                    value={fileDescription}
                    onChange={(e) => setFileDescription(e.target.value)}
                    placeholder="Describe la evidencia..."
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleUploadFile}
                    disabled={!file || uploading}
                    startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                    fullWidth
                  >
                    {uploading ? 'Subiendo...' : 'Subir y Crear Evidencia'}
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Asunto"
                    value={emailData.subject}
                    onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="De (Remitente) *"
                    required
                    value={emailData.from}
                    onChange={(e) => setEmailData({ ...emailData, from: e.target.value })}
                    error={!emailData.from}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Para (Destinatario) *"
                    required
                    value={emailData.to}
                    onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                    error={!emailData.to}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <DatePicker
                      label="Fecha del correo"
                      value={emailData.date}
                      onChange={(date) => setEmailData({ ...emailData, date: date || new Date() })}
                      slotProps={{
                        textField: {
                          fullWidth: true
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cuerpo del correo"
                    multiline
                    rows={6}
                    value={emailData.body}
                    onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                    placeholder="Pega aquí el contenido del correo electrónico..."
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción (opcional)"
                    multiline
                    rows={2}
                    value={emailDescription}
                    onChange={(e) => setEmailDescription(e.target.value)}
                    placeholder="Describe la evidencia..."
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleCreateEmail}
                    disabled={!emailData.from || !emailData.to || uploading}
                    startIcon={uploading ? <CircularProgress size={20} /> : <EmailIcon />}
                    fullWidth
                  >
                    {uploading ? 'Creando...' : 'Crear Evidencia de Correo'}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </TabPanel>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
      </Dialog>

      {/* Modal para ver detalles de correo */}
      <Dialog
        open={emailViewModalOpen}
        onClose={() => {
          setEmailViewModalOpen(false);
          setSelectedEmailEvidence(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Detalles del Correo Electrónico
            </Typography>
            <IconButton onClick={() => {
              setEmailViewModalOpen(false);
              setSelectedEmailEvidence(null);
            }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEmailEvidence && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Asunto:</strong>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedEmailEvidence.emailSubject || '—'}
                </Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>De:</strong>
                </Typography>
                <Typography variant="body1">
                  {selectedEmailEvidence.emailFrom || '—'}
                </Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Para:</strong>
                </Typography>
                <Typography variant="body1">
                  {selectedEmailEvidence.emailTo || '—'}
                </Typography>
              </Grid>
              
              {selectedEmailEvidence.emailDate && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Fecha:</strong>
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedEmailEvidence.emailDate).toLocaleString('es-MX')}
                  </Typography>
                </Grid>
              )}
              
              {selectedEmailEvidence.emailBody && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Cuerpo del correo:</strong>
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: '300px', overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedEmailEvidence.emailBody}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              
              {selectedEmailEvidence.description && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Descripción:</strong> {selectedEmailEvidence.description}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => {
            setEmailViewModalOpen(false);
            setSelectedEmailEvidence(null);
          }} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClaimEvidenceModal;

